import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { X, Calendar, Flag, Tag, Trash2 } from 'lucide-react';
import { useTaskStore } from '../stores/taskStore';
import LoadingSpinner from './LoadingSpinner';

const TaskModal = ({ isOpen, onClose, onSubmit, onDelete, task = null }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { categories, fetchCategories } = useTaskStore();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      priority: 'medium',
      status: 'todo',
      dueDate: '',
      categoryIds: [],
    },
  });

  const selectedCategories = watch('categoryIds');

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      if (task) {
        // Populate form with task data for editing
        setValue('title', task.title);
        setValue('description', task.description || '');
        setValue('priority', task.priority);
        setValue('status', task.status);
        setValue('dueDate', task.dueDate ? task.dueDate.split('T')[0] : '');
        setValue('categoryIds', task.categories?.map(cat => cat.id) || []);
      }
    }
  }, [isOpen, task, fetchCategories, setValue]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDelete = async () => {
    if (task && onDelete && window.confirm('Are you sure you want to delete this task?')) {
      setIsLoading(true);
      const result = await onDelete(task.id);
      if (result.success) {
        onClose();
      }
      setIsLoading(false);
    }
  };

  const onFormSubmit = async (data) => {
    setIsLoading(true);
    
    const taskData = {
      ...data,
      dueDate: data.dueDate ? new Date(data.dueDate).toISOString() : null,
      categoryIds: data.categoryIds.map(id => parseInt(id)),
    };

    const result = await onSubmit(taskData);
    
    if (result.success) {
      reset();
      onClose();
    }
    
    setIsLoading(false);
  };

  const toggleCategory = (categoryId) => {
    const currentIds = selectedCategories || [];
    const newIds = currentIds.includes(categoryId)
      ? currentIds.filter(id => id !== categoryId)
      : [...currentIds, categoryId];
    setValue('categoryIds', newIds);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-6 text-center sm:block sm:p-0">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={handleClose}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl z-[70]"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {task ? 'Edit Task' : 'Create New Task'}
                </h3>
                <button
                  onClick={handleClose}
                  className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    {...register('title', { required: 'Title is required' })}
                    type="text"
                    className="input"
                    placeholder="Enter task title"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="input resize-none"
                    placeholder="Enter task description (optional)"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Flag className="w-4 h-4 inline mr-1" />
                      Priority
                    </label>
                    <select {...register('priority')} className="input">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select {...register('status')} className="input">
                      <option value="todo">To Do</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Due Date
                  </label>
                  <input
                    {...register('dueDate')}
                    type="date"
                    className="input"
                  />
                </div>

                {categories.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Tag className="w-4 h-4 inline mr-1" />
                      Categories
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => toggleCategory(category.id)}
                          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                            selectedCategories?.includes(category.id)
                              ? 'text-white'
                              : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                          }`}
                          style={{
                            backgroundColor: selectedCategories?.includes(category.id)
                              ? category.color
                              : undefined,
                          }}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-between pt-4">
                  <div>
                    {task && onDelete && (
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={isLoading}
                        className="btn-outline text-red-600 border-red-300 hover:bg-red-50 flex items-center space-x-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="btn-outline"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="btn-primary flex items-center space-x-2"
                    >
                      {isLoading ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <span>{task ? 'Update' : 'Create'} Task</span>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TaskModal;
